import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  addDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  limit,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { db, auth } from '../firebase';

const useFirestoreHistory = () => {
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setChatHistory([]);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Load chat history
  const loadHistory = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const historyRef = collection(db, 'users', userId, 'qr_history');
      const q = query(historyRef, orderBy('timestamp', 'desc'), limit(20));
      
      const snapshot = await getDocs(q);
      const histories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      
      setChatHistory(histories);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading history:', error);
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Save a new chat session
  const saveChatSession = useCallback(async (promptText, qrCodes, aiResponse) => {
    if (!userId) {
      return null;
    }

    try {
      const historyRef = collection(db, 'users', userId, 'qr_history');
      
      const title = promptText.length > 50 
        ? promptText.substring(0, 50) + '...' 
        : promptText;

      const docRef = await addDoc(historyRef, {
        title,
        promptText,
        qrCodes: qrCodes.map(qr => ({
          id: qr.id || null,
          qrContent: qr.qrContent || '',
          size: qr.size || 256,
          bgColor: qr.bgColor || '#ffffff',
          fgColor: qr.fgColor || '#000000',
          level: qr.level || 'M',
          isDynamic: qr.isDynamic || false,
          qrType: qr.qrType || 'static',
          shortId: qr.shortId || null,
          redirectUrl: qr.redirectUrl || null,
          originalContent: qr.originalContent || null,
          originalPrompt: qr.originalPrompt || null,
          timestamp: qr.timestamp || new Date().toISOString()
        })),
        aiResponse: aiResponse || '',
        timestamp: serverTimestamp(),
        createdAt: new Date().toISOString()
      });

      // Reload history after saving
      await loadHistory();
      
      return docRef.id;
    } catch (error) {
      console.error('Error saving chat session:', error);
      return null;
    }
  }, [userId, loadHistory]);

  // Delete a chat session
  const deleteChatSession = useCallback(async (sessionId) => {
    if (!userId) return false;

    try {
      const docRef = doc(db, 'users', userId, 'qr_history', sessionId);
      await deleteDoc(docRef);
      
      // Reload history after deleting
      await loadHistory();
      
      return true;
    } catch (error) {
      console.error('Error deleting session:', error);
      return false;
    }
  }, [userId, loadHistory]);

  // Get relative time string
  const getRelativeTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return {
    chatHistory,
    isLoading,
    saveChatSession,
    deleteChatSession,
    getRelativeTime,
    userId,
    refreshHistory: loadHistory
  };
};

export default useFirestoreHistory;